/**
 * Who tells about something that happened, so it is told once and in the right place. A screen that already
 * shows it (the static QR of that key, the result screen of that collection or payout, the page of that
 * batch) tells it itself; a toast in the corner tells it everywhere else. Two tools decide it.
 *
 * - **What is on screen**, in this tab. Those screens register what they show while they show it, and the toast
 *   stays quiet for it.
 * - **What was already announced**, across tabs (`localStorage`), so two open tabs do not both chime for the
 *   same thing. A screen that shows it marks it as soon as it reads it; a toast waits a moment before
 *   claiming it, so that screen gets there first.
 *
 * Both count only while the tab is visible: a screen left open in a tab nobody is looking at tells nobody, so
 * the toast goes where the person is (another tab, or this one when they come back to it).
 */

function looking(): boolean {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

const onScreen = new Map<string, number>();

function hold(token: string, change: 1 | -1) {
  const next = (onScreen.get(token) ?? 0) + change;
  if (next > 0) onScreen.set(token, next);
  else onScreen.delete(token);
}

/**
 * What a screen can be showing. A Bre-B key is named by its row and by its value, since older payins carry
 * only the value.
 */
export const screenTokens = {
  brebKey: (key: { id: string; key_value: string }) => [
    `breb-key:${key.id}`,
    `breb-key-value:${key.key_value}`,
  ],
  brebKeyOf: (payment: { receivingKeyId: string | null; receivingKeyValue: string | null }) => [
    ...(payment.receivingKeyId ? [`breb-key:${payment.receivingKeyId}`] : []),
    ...(payment.receivingKeyValue ? [`breb-key-value:${payment.receivingKeyValue}`] : []),
  ],
  payin: (id: string) => [`payin:${id}`],
  payout: (id: string) => [`payout:${id}`],
  payoutBatch: (id: string) => [`payout-batch:${id}`],
};

/** Registers what a screen shows; the returned function takes it off again. */
export function showOnScreen(tokens: readonly string[]): () => void {
  tokens.forEach((token) => hold(token, 1));
  return () => tokens.forEach((token) => hold(token, -1));
}

/** Whether a screen in this tab shows one of these, to someone looking at it. */
export function isOnScreen(tokens: readonly string[]): boolean {
  return looking() && tokens.some((token) => onScreen.has(token));
}

const STORAGE_KEY = "coloca:dashboard-alerts-announced";
/** Enough to cover everything a few open tabs could be telling about at the same time. */
const REMEMBERED = 100;
// Also kept in memory, so a browser that refuses storage still announces each thing once per tab.
const announcedHere = new Set<string>();

function readAnnounced(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function record(announcementId: string): void {
  announcedHere.add(announcementId);
  const announced = readAnnounced();
  if (announced.includes(announcementId)) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([announcementId, ...announced].slice(0, REMEMBERED)));
  } catch {
    // Without storage each tab decides alone; the memory set above still stops repeats within it.
  }
}

/** A screen showed it: no toast tells it again, here or in another tab. Ignored while the tab is hidden. */
export function markAnnounced(announcementId: string): void {
  if (looking()) record(announcementId);
}

type ClaimLocks = Pick<LockManager, "request">;

const browserLocks = (): ClaimLocks | null =>
  typeof navigator !== "undefined" && navigator.locks ? navigator.locks : null;

/**
 * Takes it for this tab: true when nobody has told about it yet, and from now on it is taken. Two visible
 * tabs get the same push at the same moment, so the read and the write run under one lock that every tab of
 * the browser shares (Web Locks); without a lock manager they run as they are.
 */
export async function claimAnnouncement(
  announcementId: string,
  locks: ClaimLocks | null = browserLocks(),
): Promise<boolean> {
  const claim = () => {
    if (announcedHere.has(announcementId) || readAnnounced().includes(announcementId)) return false;
    record(announcementId);
    return true;
  };
  if (!locks) return claim();
  try {
    return await locks.request("coloca:dashboard-alert-claim", claim);
  } catch {
    return claim();
  }
}
